<template>
  <div class="colour-picker-container" ref="pickerContainer" :style="colourPickerContainerStyle()">
    <div class="colour-picker" ref="picker">
      <ColourPickerOption
        v-for="(colourPickerOption, index) in this.colourPickerOptions"
        :key="index"
        :colourPalette="colourPickerOption"
        :secondaryColours="secondaryColours"
        :isSelected="isSelected(colourPickerOption)"
        :style="colourPickerOptionStyle(isSelected(colourPickerOption))"
        :colourPickerOptionsType="colourPickerOptionsType"
        class="colour-picker-option"
      />
    </div>
  </div>
</template>

<script>
import ColourPickerOption from '@/components/AvatarBuilderUI/ColourPickerOption.vue';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import { ColourPickerOptionsType } from '@/services/ColourPickerOptionsHelper';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import TraitsHelper from '@/services/TraitsHelper';

export default {
  name: 'ColourPicker',
  props: [
    'colourPickerConfig', //: ColourPickerConfig
    'isOutfitSharingButtonVisible', // :Boolean
  ],
  components: {
    ColourPickerOption,
  },
  methods: {
    colourPickerOptionStyle(isSelected) {
      if (isSelected) {
        // Expand the selected colour picker option.
        return { width: '24px', height: '24px' };
      } else {
        return { width: '26px', height: '26px' };
      }
    },
    isSelected(colourPickerOption) {
      switch (this.colourPickerOptionsType) {
        case ColourPickerOptionsType.FACIAL_FEATURES:
          return this.isColourPickerOptionSelectedFacialFeature(colourPickerOption);
        case ColourPickerOptionsType.FASHION:
          return this.isColourPickerOptionSelectedFashion(colourPickerOption);
        case ColourPickerOptionsType.TRAITS:
          return this.isColourPickerOptionSelectedTraits(colourPickerOption);
      }
    },
    isColourPickerOptionSelectedFashion(colourPickerOption) {
      // Check whether all of the colours in the palette match the selected palette.
      for (let key of Object.keys(colourPickerOption)) {
        if (colourPickerOption[key] !== this.selectedColourPickerOptionForFashionItem[key]) {
          return false;
        }
      }
      return true;
    },
    isColourPickerOptionSelectedFacialFeature(colourPickerOption) {
      return this.selectedColourPickerOptionForFacialFeatureItem === colourPickerOption;
    },
    isColourPickerOptionSelectedTraits(colourPickerOption) {
      // Similarly to fashion, we must check that each colour within the current option is identical
      // to the previously selected option
      for (let i = 0; i < colourPickerOption.length; i++) {
        if (colourPickerOption[i] !== this.selectedColourPickerOptionForTraitItem[i]) {
          return false;
        }
      }
      return true;
    },
    colourPickerContainerStyle() {
      if (this.isOutfitSharingButtonVisible) {
        return { height: 'calc(48% - 40px)' };
      } else {
        return { height: '48%' };
      }
    },
  },
  computed: {
    selectedCategory() {
      return this.$store.getters.selectedCategory;
    },
    colourPickerOptionsType() {
      return this.colourPickerConfig.type;
    },
    colourPickerOptions() {
      return this.colourPickerConfig.options;
    },
    secondaryColours() {
      return this.colourPickerConfig.secondaryColours;
    },
    selectedColourPickerOptionForFashionItem() {
      // Retrieve the selected colour picker option for the current item if one exists
      const clothingCategory = MixAndMatchHelper.getItemCategory(this.selectedCategory);
      const selectedColourPickerOption = MixAndMatchHelper.getSelectedColourPickerOptionFromTraits(
        clothingCategory,
        this.$store.getters.traits,
      );
      // Default to first option if none is selected
      return selectedColourPickerOption || this.colourPickerOptions[0];
    },
    selectedColourPickerOptionForFacialFeatureItem() {
      // Retrieves the selected colour picker option for the current item if one exists
      const selectedCategory = this.selectedCategory;
      const groupId = FacialFeaturesHelper.getSelectedGroupIdForCategory(
        selectedCategory,
        this.$store.getters.facialFeatureTraits,
      );

      if (groupId) {
        return FacialFeaturesHelper.getSelectedColourForGroup(
          selectedCategory,
          groupId,
          this.$store.getters.facialFeatureTraits,
        );
      }
      return this.colourPickerOptions[0];
    },
    selectedColourPickerOptionForTraitItem() {
      const selectedColourPickerOption = TraitsHelper.getSelectedColourPickerOptionFromTraits(
        this.selectedCategory,
        this.$store.getters.traits,
      );
      return selectedColourPickerOption || this.colourPickerOptions[0];
    },
  },
};
</script>

<style scoped>
.colour-picker-container {
  width: 48px;
  top: 0pt;
  right: 4px;
  position: absolute;
  overflow-y: scroll;
  /* Add bounce to colour picker scroll. */
  -webkit-overflow-scrolling: touch;
  /* The gradient masks fade out the edges of the colour picker. */
  -webkit-mask-image: linear-gradient(
    transparent 0%,
    transparent 40pt,
    rgba(0, 0, 0, 1) 50pt,
    rgba(0, 0, 0, 1) 95%,
    transparent 100%
  );
  mask-image: linear-gradient(
    transparent 0%,
    transparent 40pt,
    rgba(0, 0, 0, 1) 50pt,
    rgba(0, 0, 0, 1) 95%,
    transparent 100%
  );
}

.colour-picker {
  width: 100%;
  position: relative;
  flex-direction: column;
  align-items: center;
  display: flex;
  /* The top padding allows the colour picker to show below the save button and fade out behind it. */
  padding-top: 56px;
  /* The bottom padding allows the user to scroll the colour picker above the bottom gradient. */
  padding-bottom: 8px;
}

.colour-picker-option {
  cursor: pointer;
  border-radius: 50%;
  margin: 5px 0;
  transition-duration: 0.1s;
  position: relative;
  width: 100%;
  height: 100%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.26), inset 0px 0px 0px 1px rgba(0, 0, 0, 0.06);
}
@media (orientation: landscape) {
  .colour-picker-container {
    /* The gradient masks fade out the edges of the colour picker. */
    -webkit-mask-image: linear-gradient(
      transparent 0%,
      rgba(0, 0, 0, 1) 5%,
      rgba(0, 0, 0, 1) 95%,
      transparent 100%
    );
    mask-image: linear-gradient(
      transparent 0%,
      rgba(0, 0, 0, 1) 5%,
      rgba(0, 0, 0, 1) 95%,
      transparent 100%
    );
    height: 100%;
  }

  .colour-picker {
    /* Save button is not in the preview area in landscape */
    padding-top: 8px;
  }
}
</style>
